import { useId, useState } from 'react';
import type { ClipboardEvent, KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { t } from '../../lib/i18n';

export function isValidEmail(v: string): boolean {
  return v.length <= 254 && /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]{2,}$/.test(v);
}

/** Tidies a phone number: keeps a leading +, digits and single spaces. */
export function normalisePhone(v: string): string {
  const s = v.trim();
  const plus = s.startsWith('+') ? '+' : '';
  return plus + s.replace(/[^\d ]/g, '').replace(/\s+/g, ' ').trim();
}

export function isValidPhone(v: string): boolean {
  const digits = v.replace(/\D/g, '');
  return /^\+?[\d ]+$/.test(v) && digits.length >= 7 && digits.length <= 15;
}

/** Splits pasted or typed text on commas, semicolons and line breaks. */
export function splitList(text: string): string[] {
  return text
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

interface ListInputProps {
  label: string;
  value: readonly string[];
  onChange: (next: string[]) => void;
  hint?: string;
  disabled?: boolean;
  /** Most entries allowed. */
  max?: number;
  id?: string;
}

function ListInput({
  label,
  value,
  onChange,
  hint,
  disabled,
  max,
  id,
  inputType,
  inputMode,
  normalise,
  isValid,
  invalidKey,
  defaultHintKey,
}: ListInputProps & {
  inputType: 'email' | 'tel';
  inputMode: 'email' | 'tel';
  normalise: (v: string) => string;
  isValid: (v: string) => boolean;
  invalidKey: string;
  defaultHintKey: string;
}) {
  const auto = useId();
  const base = id ?? auto;
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [announce, setAnnounce] = useState('');
  const full = max !== undefined && value.length >= max;

  const commit = (texts: string[]): boolean => {
    let list = [...value];
    let problem: string | undefined;
    const added: string[] = [];
    for (const raw of texts) {
      const v = normalise(raw);
      if (!isValid(v)) {
        problem = t(invalidKey, { value: raw });
        break;
      }
      if (list.some((x) => x.toLowerCase() === v.toLowerCase())) {
        problem = t('p2w.list.duplicate', { value: v });
        continue;
      }
      if (max !== undefined && list.length >= max) {
        problem = t('p2w.list.max', { max });
        break;
      }
      list = [...list, v];
      added.push(v);
    }
    if (added.length) {
      onChange(list);
      setAnnounce(t('p2w.list.added', { value: added.join(', ') }));
    }
    setError(problem);
    return problem === undefined;
  };

  const submit = () => {
    const parts = splitList(draft);
    if (parts.length === 0) return;
    if (commit(parts)) setDraft('');
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      submit();
    } else if (e.key === 'Backspace' && draft === '' && value.length) {
      e.preventDefault();
      const last = value[value.length - 1] ?? '';
      onChange(value.slice(0, -1));
      setAnnounce(t('p2w.list.removed', { value: last }));
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (/[,;\n]/.test(text)) {
      e.preventDefault();
      if (commit(splitList(text))) setDraft('');
    }
  };

  const remove = (v: string) => {
    onChange(value.filter((x) => x !== v));
    setAnnounce(t('p2w.list.removed', { value: v }));
  };

  const describedBy = [`${base}-hint`, error ? `${base}-err` : ''].filter(Boolean).join(' ');
  return (
    <div>
      <label htmlFor={`${base}-in`} className="mb-1 block text-sm font-medium text-slate-800">
        {label}
      </label>
      {value.length > 0 ? (
        <ul aria-label={label} className="mb-2 flex flex-wrap gap-2">
          {value.map((v) => (
            <li key={v} className="inline-flex items-center gap-1 rounded-full bg-slate-100 py-1 pl-3 pr-1 text-sm text-slate-800">
              <span>{v}</span>
              <button
                type="button"
                disabled={disabled}
                aria-label={t('p2w.list.remove', { value: v })}
                onClick={() => remove(v)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-600 hover:bg-slate-200 disabled:opacity-50"
              >
                <X aria-hidden="true" className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex gap-2">
        <input
          id={`${base}-in`}
          type={inputType}
          inputMode={inputMode}
          autoComplete="off"
          value={draft}
          disabled={disabled || full}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          onPaste={onPaste}
          onBlur={() => {
            if (draft.trim()) submit();
          }}
          className="block min-h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 hover:border-slate-400 disabled:bg-slate-100 aria-[invalid=true]:border-red-600 sm:min-h-9"
        />
        <button
          type="button"
          disabled={disabled || full || draft.trim() === ''}
          onClick={submit}
          className="inline-flex min-h-10 items-center rounded-md border border-slate-300 bg-white px-3.5 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50 sm:min-h-9"
        >
          {t('p2w.list.add')}
        </button>
      </div>
      <p id={`${base}-hint`} className="mt-1 text-xs text-slate-600">
        {hint ?? t(defaultHintKey)}
      </p>
      {error ? (
        <p id={`${base}-err`} className="mt-1 text-sm text-red-700">
          {error}
        </p>
      ) : null}
      <span role="status" className="sr-only">
        {announce}
      </span>
    </div>
  );
}

/** List of email addresses (chips). Enter, comma, semicolon, paste or leaving the box adds; duplicates are ignored. */
export function EmailListInput(props: ListInputProps) {
  return (
    <ListInput
      {...props}
      inputType="email"
      inputMode="email"
      normalise={(v) => v.trim()}
      isValid={isValidEmail}
      invalidKey="p2w.list.emailInvalid"
      defaultHintKey="p2w.list.emailHint"
    />
  );
}

/** List of phone numbers (chips), kept as typed apart from tidied spacing. */
export function PhoneListInput(props: ListInputProps) {
  return (
    <ListInput
      {...props}
      inputType="tel"
      inputMode="tel"
      normalise={normalisePhone}
      isValid={isValidPhone}
      invalidKey="p2w.list.phoneInvalid"
      defaultHintKey="p2w.list.phoneHint"
    />
  );
}
